import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from '../baseQuery';

export const studentApi = createApi({
	reducerPath: 'studentApi',
	baseQuery: axiosBaseQuery,
	tagTypes: [
		'StudentDashboard',
		'StudentSessions',
		'StudentCurricula',
		'StudentCurriculumDetail',
		'StudentProfile',
	],
	endpoints: (builder) => ({
		getStudentDashboard: builder.query<any, void>({
			query: () => ({ url: '/student/dashboard' }),
			providesTags: ['StudentDashboard'],
		}),
		getStudentSessions: builder.query<any, { skip?: number; limit?: number }>({
			query: ({ skip = 0, limit = 20 }) => ({
				url: `/student/sessions?skip=${skip}&limit=${limit}`,
			}),
			providesTags: ['StudentSessions'],
		}),
		getStudentSession: builder.query<any, string>({
			query: (id) => ({ url: `/student/sessions/${id}` }),
		}),

		// ── Curriculum browsing ─
		getStudentCurricula: builder.query<any, void>({
			query: () => ({ url: '/student/curricula' }),
			providesTags: ['StudentCurricula'],
		}),
		getStudentCurriculumDetail: builder.query<any, string>({
			query: (id) => ({ url: `/student/curricula/${id}` }),
			providesTags: (_result, _err, id) => [
				{ type: 'StudentCurriculumDetail', id },
			],
		}),
		// ── Profile ─
		getStudentProfile: builder.query<any, void>({
			query: () => ({ url: '/student/profile' }),
			providesTags: ['StudentProfile'],
		}),
		updateStudentProfile: builder.mutation<any, Record<string, any>>({
			query: (body) => ({
				url: '/student/profile',
				method: 'PUT',
				data: body,
			}),
			invalidatesTags: ['StudentProfile'],
		}),
		// ── Session flow ─
		startSession: builder.mutation<
			any,
			{ curriculumId: string; moduleIndex: number; sessionIndex: number }
		>({
			query: (body) => ({
				url: '/student/sessions/start',
				method: 'POST',
				data: body,
			}),
			invalidatesTags: ['StudentSessions', 'StudentCurriculumDetail'],
		}),
		getSessionSteps: builder.query<any, string>({
			query: (id) => ({ url: `/student/sessions/${id}/steps` }),
		}),
		completeStep: builder.mutation<
			any,
			{ sessionId: string; stepIndex: number; response?: unknown }
		>({
			query: ({ sessionId, stepIndex, response }) => ({
				url: `/student/sessions/${sessionId}/complete-step`,
				method: 'PUT',
				data: { stepIndex, response },
			}),
		}),
		completeSession: builder.mutation<any, string>({
			query: (sessionId) => ({
				url: `/student/sessions/${sessionId}/complete`,
				method: 'PUT',
			}),
			invalidatesTags: [
				'StudentSessions',
				'StudentCurricula',
				'StudentCurriculumDetail',
			],
		}),
	}),
});

export const {
	useGetStudentDashboardQuery,
	useGetStudentSessionsQuery,
	useGetStudentSessionQuery,
	useGetStudentCurriculaQuery,
	useGetStudentCurriculumDetailQuery,
	useGetStudentProfileQuery,
	useUpdateStudentProfileMutation,
	useStartSessionMutation,
	useGetSessionStepsQuery,
	useCompleteStepMutation,
	useCompleteSessionMutation,
} = studentApi;
